import React, { useState } from "react";
import "./navigation.css";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { SmartQuest, UserAvatar, RightVector } from "../../../assets";
import Spinner from "../Spinner/Spinner";
import { useGetMe } from "../../../hooks/useGetMe";
import { useNotifications } from "../../../hooks/useNotifications";
import {
  FaCommentAlt,
  FaBell,
  FaChevronDown,
  FaUniversity,
  FaWpforms,
  FaUsers,
  FaDatabase,
  FaEnvelope,
  FaChevronLeft,
  FaChevronRight,
  FaSignOutAlt,
} from "react-icons/fa";

const Navigation = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useGetMe();
  const [collapsed, setCollapsed] = useState(false);

  const { messageNotifs, responseNotifs, setMessageNotifs, setResponseNotifs } =
    useNotifications(user?.id);

  const [showMessages, setShowMessages] = useState(false);
  const [showResponses, setShowResponses] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  if (loading) return <Spinner size="lg" color="#EF4346" />;

  return (
    <>
      <nav className="navbar bg-body" style={{ height: "60px" }}>
        <div className="container-fluid mt-2 mb-4">
          {/* Left: Logo */}
          <div className="d-flex align-items-center justify-content-start ms-2 cursor-pointer">
            <NavLink to="/home">
              <img
                src={SmartQuest}
                alt="SmartQuest Name"
                className="logoName_nav"
              />
            </NavLink>
          </div>

          {/* Right: Notifications & User */}
          <div className="d-flex align-items-center justify-content-end position-relative">
            {/* Messages */}
            <div className="notif-wrapper mx-2">
              <div
                className="notif-icon-container"
                onClick={() => {
                  setShowMessages((prev) => !prev);
                  setShowResponses(false);
                  setShowUserMenu(false);
                }}
              >
                <FaCommentAlt className="notif-icon" />
                {messageNotifs.length > 0 && (
                  <span className="notif-badge">{messageNotifs.length}</span>
                )}
              </div>

              {showMessages && (
                <div className="notif-dropdown">
                  <div className="notif-header">📨 Nouveaux messages</div>
                  {messageNotifs.length === 0 ? (
                    <div className="notif-empty">Aucun nouveau message</div>
                  ) : (
                    messageNotifs.map((msg, index) => (
                      <div
                        key={index}
                        className="notif-item"
                        onClick={() => {
                          navigate("/messagerie");
                          setShowMessages(false);
                        }}
                      >
                        <strong>De:</strong> {msg.from || "Utilisateur inconnu"}
                        <div className="notif-content">{msg.content}</div>
                      </div>
                    ))
                  )}
                  <div
                    className="notif-footer"
                    onClick={() => setMessageNotifs([])}
                  >
                    Marquer comme lu
                  </div>
                </div>
              )}
            </div>

            {/*  Form responses */}
            <div className="notif-wrapper mx-3">
              <div
                className="notif-icon-container"
                onClick={() => {
                  setShowResponses((prev) => !prev);
                  setShowMessages(false);
                  setShowUserMenu(false);
                }}
              >
                <FaBell className="notif-icon" />
                {responseNotifs.length > 0 && (
                  <span className="notif-badge">{responseNotifs.length}</span>
                )}
              </div>

              {showResponses && (
                <div className="notif-dropdown">
                  <div className="notif-header">📄 Nouvelles réponses</div>
                  {responseNotifs.length === 0 ? (
                    <div className="notif-empty">Aucune nouvelle réponse</div>
                  ) : (
                    responseNotifs.map((r, i) => (
                      <div
                        key={i}
                        className="notif-item"
                        onClick={() => {
                          navigate("/formulaires");
                          setShowResponses(false);
                        }}
                      >
                        <strong>Formulaire :</strong>{" "}
                        {r.formTitle || "Formulaire inconnu"}
                        <div className="notif-content">
                          Soumis par {r.submitted_by || "Utilisateur inconnu"}
                        </div>
                      </div>
                    ))
                  )}
                  <div
                    className="notif-footer"
                    onClick={() => setResponseNotifs([])}
                  >
                    Marquer comme lu
                  </div>
                </div>
              )}
            </div>

            {/*  User Info */}
            <div className="user-menu-wrapper mx-2 position-relative">
              {user && (
                <>
                  <div
                    className="d-flex align-items-center cursor-pointer user-toggle"
                    onClick={() => {
                      setShowUserMenu((prev) => !prev);
                      setShowMessages(false);
                      setShowResponses(false);
                    }}
                  >
                    {/*  On large screens: name + avatar */}
                    <span className="user-name d-none d-md-inline">
                      Dr. {user.first_name} {user.last_name}
                    </span>

                    {/*  Avatar always visible */}
                    <img
                      src={UserAvatar}
                      alt="Profile"
                      className="mx-2 rounded-circle"
                      style={{ width: "35px", height: "35px" }}
                    />
                    <FaChevronDown className="user-chevron d-none d-md-inline" />
                  </div>

                  {showUserMenu && (
                    <div className="user-dropdown">
                      {/* Show name only on small screens */}
                      <div
                        className="user-dropdown-item d-md-none"
                        style={{ cursor: "default" }}
                      >
                        <strong>
                          Dr. {user.first_name} {user.last_name}
                        </strong>
                      </div>

                      <div
                        className="user-dropdown-item"
                        onClick={() => {
                          localStorage.removeItem("access_token");
                          navigate("/");
                        }}
                      >
                        <FaSignOutAlt className="me-2" /> Déconnexion
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Sidebar and main content */}
      <div className="container-fluid d-flex">
        <aside className={`sidebar ${collapsed ? "collapsed" : ""}`}>
          <ul className="sidebar-menu">
            <li>
              <NavLink
                to="/etudes"
                className={
                  location.pathname.startsWith("/etudes") ? "active" : ""
                }
              >
                <FaUniversity className="icon-sidebar me-2" />
                {!collapsed && "Etudes"}
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/formulaires"
                className={
                  location.pathname.startsWith("/formulaires") ? "active" : ""
                }
              >
                <FaWpforms className="icon-sidebar me-2" />
                {!collapsed && "Formulaires"}
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/collaborateurs"
                className={({ isActive }) => (isActive ? "active" : "")}
              >
                <FaUsers className="icon-sidebar me-2" />
                {!collapsed && "Collaborateurs"}
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/bases-de-donnees"
                className={({ isActive }) => (isActive ? "active" : "")}
              >
                <FaDatabase className="icon-sidebar me-2" />
                {!collapsed && "Bases de données"}
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/messagerie"
                className={({ isActive }) => (isActive ? "active" : "")}
              >
                <FaEnvelope className="icon-sidebar me-2" />
                {!collapsed && "Messagerie"}
              </NavLink>
            </li>
          </ul>

          {/* Toggle sidebar */}
          <button
            className="collapse-btn"
            onClick={() => setCollapsed((prev) => !prev)}
          >
            {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>
        </aside>

        {/* Right vector background */}
        <img
          src={RightVector}
          alt="Right Vector"
          style={{
            position: "fixed",
            top: "60px",
            right: "0px",
            height: "100vh",
            zIndex: 9999,
            pointerEvents: "none",
          }}
        />
        {children}
      </div>
    </>
  );
};

export default Navigation;
